import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapRef, Layer, Source, Marker } from 'react-map-gl'
import bboxPolygon from '@turf/bbox-polygon'
import { point } from '@turf/helpers'
import { Feature, Point, Polygon } from 'geojson'
import { useQueryClient } from '@tanstack/react-query'
import { ModalDialogContainer } from '@common/modal'
import { useGetW3WToCoords, w3wKeys } from '@core/react-query/features/w3w'
import { useProjectMapPermits } from '@core/react-query/features/permits'
import {
  usePermitStatusFilter,
  usePermitTemplatesFilter,
  useDateRangeFilter,
  useFilterValueStore,
} from '@core/providers/filters'
import useVisibleEntitiesOnMap from '@core/providers/useVisibleEntitiesOnMap'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { DialogContent, DialogTitle, IconButton, Popper } from '@mui/material'
import { TextField, Button } from '@common/material'
import { Box, Flex, Image } from '@fundamentals'
import {
  Map,
  GeoPolygonLayer,
  GeoPointMarkerLayer,
} from '@elements/Geolocation'
import { PermitCard } from '@elements'
import { What3wordsAutosuggest } from '@what3words/react-components'
import { localizedStrings } from '@core/strings'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Permit } from '@core/entities/permit/Permit'
import { PermitDetailsModal } from '@modules/permitDetails/PermitDetailsModal'
import { InitialViewState } from '@core/entities/project/ProjectLocationNode/ProjectLocationNodeBoundary'
import {
  tryCatch,
  calculatePositionOfPopper,
  setupClickEffect,
  setupHoverEffect,
} from '@modules/geolocation'
import {
  PermitFilterToolbar,
  PermitListSidebar,
  usePermitMapFeatures,
  permitMapLayerIds,
  permitMapSourceIds,
  PERMIT_MARKERS_ID,
  PERMIT_ZONES_ID,
} from '@modules/geolocation/permits'

type W3WMapModalProps = {
  projectId: number
  organisationId: number
  initialWhat3Words: string
  onChangeWhat3Words: (what3words: string) => void
  initialViewState?: InitialViewState | null
}

const what3WordsRegex =
  /^\/{0,}[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]{1,}$/

export const W3WMapModal: React.FC<W3WMapModalProps> = ({
  projectId,
  organisationId,
  initialWhat3Words,
  onChangeWhat3Words,
  initialViewState,
}) => {
  const mapRef = useRef<MapRef>()
  const w3wAutoSuggestRef = useRef<HTMLWhat3wordsAutosuggestElement>(null)
  const cleanupClickEffectRef = useRef<(() => void) | null>(null)
  const cleanupHoverEffectRef = useRef<(() => void) | null>(null)
  const queryClient = useQueryClient()
  const { showModal, hideModal } = useModal()
  const toast = useToast()
  const [what3words, setWhat3Words] = useState<string>(initialWhat3Words)
  const [gridData, setGridData] = useState(null)
  const [marker, setMarker] = useState<Feature<Point>>(null)
  const [highlightedGridCellUnderMarker, setHighlightedGridCellUnderMarker] =
    useState<Feature<Polygon>>(null)
  const [showExistingPermitsOnMap, setShowExistingPermitsOnMap] = useState(true)
  const [hoveredEntityId, setHoveredEntityId] = useState<
    string | number | null
  >(null)
  const [popupEntity, setPopupEntity] = useState<{
    id: number
    clientX: number
    clientY: number
  }>(null)

  const filters = {
    status: usePermitStatusFilter({
      multiple: true,
    }),
    permitTemplate: usePermitTemplatesFilter({
      isPinned: true,
      label: localizedStrings.permitType,
      multiple: true,
      scope: {
        type: 'PROJECT',
        organisationId,
        projectId,
      },
    }),
    dateRange: useDateRangeFilter({
      isPinned: true,
      label: localizedStrings.dateRange,
      dataTest: 'date-range-filter',
    }),
  }

  const store = useFilterValueStore({
    filters,
    initialValues: {
      status: [
        'OPEN',
        'REQUESTED',
        'ISSUED',
        'EXTENSION_REQUESTED',
        'SENT_TO_PERMIT_HOLDER',
      ],
    },
  })

  const { data: permitsWithGeolocation } = useProjectMapPermits(
    {
      projectId,
      templateIds: store.values.permitTemplate?.length
        ? store.values.permitTemplate
        : null,
      permitStatuses: store.values.status?.length ? store.values.status : null,
      startTime: store?.values?.dateRange?.startTime || null,
      endTime: store?.values?.dateRange?.endTime || null,
    },
    {
      onError: (e) => {
        toast.error(e.message)
      },
    },
  )

  const { setMapBounds, visibleEntitiesOnMap: visiblePermitsOnMap } =
    useVisibleEntitiesOnMap(permitsWithGeolocation)

  const { zones: permitZones, markers: permitMarkers } = usePermitMapFeatures(
    permitsWithGeolocation,
  )

  const hoveredPermit = useMemo(() => {
    if (!hoveredEntityId || !permitsWithGeolocation) {
      return null
    }

    return permitsWithGeolocation?.find(
      (permit) => permit.id === hoveredEntityId,
    )
  }, [hoveredEntityId, permitsWithGeolocation])

  const popupPermit = useMemo(() => {
    if (!popupEntity) {
      return null
    }

    return permitsWithGeolocation?.find(
      (permit) => permit.id === popupEntity.id,
    )
  }, [popupEntity, permitsWithGeolocation])

  const handleOnPermitPopupDetails = useCallback((permit: Permit) => {
    showModal(
      <PermitDetailsModal
        permitId={permit.id}
        organisationId={permit.organisationId}
      />,
    )
    setPopupEntity(null)
  }, [])

  useGetW3WToCoords(
    { what3Words: what3words },
    {
      enabled: !!what3words,
      onSuccess: (data) => {
        const markerPoint = data?.features?.[0]

        setMarker(markerPoint)
        setHighlightedGridCellUnderMarker(bboxPolygon(markerPoint.bbox))

        mapRef?.current?.flyTo({
          center: [
            markerPoint.geometry.coordinates[0],
            markerPoint.geometry.coordinates[1],
          ],
          zoom: 18,
          duration: 1000,
        })
      },
      onError: () => {
        setMarker(null)
      },
    },
  )

  const drawGrid = async () => {
    const zoom = mapRef?.current?.getZoom()
    const loadFeatures = zoom > 17

    if (loadFeatures) {
      const bounds = mapRef?.current?.getBounds()
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const w3wBounds = {
        southwest: { lat: sw.lat, lng: sw.lng },
        northeast: { lat: ne.lat, lng: ne.lng },
      }

      try {
        const response = await queryClient.fetchQuery(
          w3wKeys.getW3WGridSection({ w3wBounds }),
        )

        setGridData(response)
      } catch (error) {
        console.error(error)
      }
    } else {
      setGridData(null)
      setHighlightedGridCellUnderMarker(null)
    }
  }

  const convertClickToWhat3Words = async (
    event: mapboxgl.MapLayerMouseEvent,
  ) => {
    const { lng, lat } = event.lngLat
    const featurePointClicked = point([lng, lat])

    const response = await queryClient.fetchQuery(
      w3wKeys.getCoordsToW3W({ point: featurePointClicked }),
    )

    if (response) {
      const markerPoint = response?.features?.[0]
      setMarker(markerPoint)
      setHighlightedGridCellUnderMarker(bboxPolygon(markerPoint.bbox))
      setWhat3Words(markerPoint.properties.words)
    }
  }

  const handleOnMapClick = (event, feature) => {
    // If a permit feature was clicked, show the permit popup only and do not
    // overwrite the currently selected W3W location.
    if (feature && permitMapSourceIds.includes(feature.source)) {
      tryCatch(() => {
        setPopupEntity({
          id: feature.properties.permitId,
          clientX: event.originalEvent.clientX,
          clientY: event.originalEvent.clientY,
        })
      })
      return
    }

    // Otherwise dismiss any open permit popup and convert the click to a W3W.
    setPopupEntity(null)
    convertClickToWhat3Words(event)
  }

  const onLoad = () => {
    if (mapRef?.current?.getZoom() > 17) {
      drawGrid()
    }

    const bounds = mapRef.current.getBounds()
    setMapBounds({
      nw: bounds.getNorthWest(),
      se: bounds.getSouthEast(),
    })

    cleanupHoverEffectRef.current = setupHoverEffect(
      mapRef.current,
      permitMapLayerIds,
      permitMapSourceIds,
      (permitId, action) => {
        if (action === 'select') {
          setHoveredEntityId(permitId)
          return
        }

        if (action === 'deselect') {
          setHoveredEntityId(null)
        }
      },
    )

    cleanupClickEffectRef.current = setupClickEffect(
      mapRef.current,
      handleOnMapClick,
    )
  }

  const onMoveEnd = () => {
    drawGrid()

    const bounds = mapRef.current?.getBounds()
    if (bounds) {
      setMapBounds({
        nw: bounds.getNorthWest(),
        se: bounds.getSouthEast(),
      })
    }
  }

  useEffect(() => {
    return () => {
      if (cleanupClickEffectRef.current) {
        cleanupClickEffectRef.current()
        cleanupClickEffectRef.current = null
      }
      if (cleanupHoverEffectRef.current) {
        cleanupHoverEffectRef.current()
        cleanupHoverEffectRef.current = null
      }
    }
  }, [])

  return (
    <ModalDialogContainer
      PaperProps={{
        sx: {
          maxWidth: '90%',
        },
      }}
    >
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.pointOfWorks}
        <IconButton
          aria-label='close-modal'
          onClick={() => {
            hideModal()
          }}
        >
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Flex flexDirection='column' height='100%' width='100%'>
          <Flex alignItems='center' justifyContent='space-between' p={1}>
            <Box>
              <What3wordsAutosuggest
                ref={w3wAutoSuggestRef}
                api_key={Project.w3wKey}
                onSelected_suggestion={(event) => {
                  setWhat3Words(event.detail.suggestion.words)
                }}
              >
                <TextField
                  value={what3words}
                  onChange={(e) => {
                    setWhat3Words(e.target.value)
                  }}
                  placeholder={localizedStrings.pointOfWorks3WordsPlaceholder}
                  label={localizedStrings.what3Words}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 400 }}
                  fullWidth
                />
              </What3wordsAutosuggest>
            </Box>

            <Button
              disabled={!what3words || !what3WordsRegex.test(what3words)}
              variant='contained'
              color='primary'
              size='large'
              sx={{ width: 200 }}
              onClick={() => {
                hideModal()
                onChangeWhat3Words(what3words)
              }}
            >
              {localizedStrings.confirm}
            </Button>
          </Flex>

          <Flex mt={2}>
            <PermitFilterToolbar
              filters={filters}
              store={store}
              showPermits={showExistingPermitsOnMap}
              setShowPermits={setShowExistingPermitsOnMap}
            />
          </Flex>

          <Flex flex={1} mt={2}>
            <Box height='100%' flex={1} mr={2}>
              <Map
                mapRef={mapRef}
                projectId={projectId}
                onLoad={onLoad}
                onMoveEnd={onMoveEnd}
                initialViewState={
                  !what3words && !!initialViewState
                    ? initialViewState
                    : undefined
                }
                serverConfig={
                  !what3words && !!initialViewState
                    ? { initialViewState: false }
                    : undefined
                }
                style={{ height: '70vh', width: '100%' }}
              >
                {showExistingPermitsOnMap &&
                  permitZones?.features?.length !== 0 && (
                    <GeoPolygonLayer
                      key={PERMIT_ZONES_ID}
                      id={PERMIT_ZONES_ID}
                      promoteId='permitId'
                      featureCollection={permitZones}
                    />
                  )}

                {showExistingPermitsOnMap &&
                  permitMarkers?.features?.length !== 0 && (
                    <GeoPointMarkerLayer
                      key={PERMIT_MARKERS_ID}
                      id={PERMIT_MARKERS_ID}
                      promoteId='permitId'
                      featureCollection={permitMarkers}
                    />
                  )}

                {gridData && (
                  <Source id='grid' type='geojson' data={gridData}>
                    <Layer
                      id='grid-layer'
                      type='line'
                      layout={{
                        'line-join': 'round',
                        'line-cap': 'round',
                      }}
                      paint={{
                        'line-color': 'orange',
                        'line-width': 1,
                      }}
                    />
                  </Source>
                )}

                {highlightedGridCellUnderMarker && (
                  <Source
                    id='highlighted-grid'
                    type='geojson'
                    data={highlightedGridCellUnderMarker}
                  >
                    <Layer
                      id='highlighted-grid-layer'
                      type='fill'
                      paint={{
                        'fill-color': '#FF0000',
                        'fill-opacity': 0.5,
                      }}
                    />
                  </Source>
                )}

                {marker && (
                  <Marker
                    anchor='bottom'
                    longitude={marker.geometry.coordinates[0]}
                    latitude={marker.geometry.coordinates[1]}
                  >
                    <Image
                      alt='point-marker'
                      src='/images/icons/markers/permit_marker_red.svg'
                    />
                  </Marker>
                )}
              </Map>
            </Box>

            {popupPermit && (
              <Popper
                id={'popup'}
                open={true}
                anchorEl={calculatePositionOfPopper(
                  popupEntity.clientX,
                  popupEntity.clientY,
                )}
                style={{ zIndex: 15000 }}
              >
                <Box width={'350px'}>
                  <PermitCard
                    templateName={popupPermit?.template?.name}
                    {...popupPermit}
                    sx={{ mx: 1, mt: 1 }}
                  >
                    <Flex mt={1}>
                      <Button
                        variant='contained'
                        size='small'
                        sx={{ flexGrow: 1, mr: 1 }}
                        endIcon={<KeyboardArrowRightIcon />}
                        onClick={() => handleOnPermitPopupDetails(popupPermit)}
                      >
                        {localizedStrings.details}
                      </Button>
                    </Flex>
                  </PermitCard>
                </Box>
              </Popper>
            )}

            {!popupPermit && hoveredPermit && (
              <Box
                width='300px'
                overflow='auto'
                position='absolute'
                sx={{ pointerEvents: 'none' }}
              >
                <PermitCard
                  templateName={hoveredPermit?.template?.name}
                  {...hoveredPermit}
                  sx={{ mx: 1, mt: 1, borderColor: 'primary.main' }}
                />
              </Box>
            )}

            <Box width='300px' overflow='auto' position='relative' mr={-2}>
              <PermitListSidebar
                mapRef={mapRef}
                permits={visiblePermitsOnMap}
                hoveredEntityId={hoveredEntityId}
                setHoveredEntityId={setHoveredEntityId}
              />
            </Box>
          </Flex>
        </Flex>
      </DialogContent>
    </ModalDialogContainer>
  )
}
